import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const JOHANNESBURG_OFFSET_MINUTES = 120;

export type FinancialWeekWindow = {
    start: Date;
    end: Date;
    weekNumber: number;
};

function inJohannesburg(value: string | Date | Dayjs): Dayjs {
    return dayjs(value).utcOffset(JOHANNESBURG_OFFSET_MINUTES);
}

function monthIndex(financialYearStartMonth: number): number {
    return ((((financialYearStartMonth - 2) % 12) + 12) % 12) + 1;
}

function firstOfStartMonth(
    year: number,
    financialYearStartMonth: number,
): Dayjs {
    const month = String(monthIndex(financialYearStartMonth) + 1).padStart(
        2,
        '0',
    );
    return dayjs(`${year}-${month}-01`)
        .utcOffset(JOHANNESBURG_OFFSET_MINUTES, true)
        .startOf('day');
}

function financialYearBounds(
    anchor: Dayjs,
    financialYearStartMonth: number,
): { start: Dayjs; end: Dayjs } {
    const year = anchor.year();
    const thisMarker = firstOfStartMonth(year, financialYearStartMonth).add(
        1 - firstOfStartMonth(year, financialYearStartMonth).day(),
        'day',
    );
    const nextMarker = firstOfStartMonth(year + 1, financialYearStartMonth).add(
        1 - firstOfStartMonth(year + 1, financialYearStartMonth).day(),
        'day',
    );

    let financialYear = year;
    if (anchor.isBefore(thisMarker)) {
        financialYear = year - 1;
    } else if (!anchor.isBefore(nextMarker)) {
        financialYear = year + 1;
    }

    const opening = firstOfStartMonth(financialYear, financialYearStartMonth);
    const start = opening.add(-opening.day(), 'day').add(1, 'day');
    const closing = firstOfStartMonth(
        financialYear + 1,
        financialYearStartMonth,
    );
    const end = closing.add(-closing.day(), 'day').endOf('day');

    return { start, end };
}

function weekContaining(
    anchor: Dayjs,
    financialYearStartMonth: number,
): { start: Dayjs; end: Dayjs; weekNumber: number } {
    const yearStart = financialYearBounds(
        anchor,
        financialYearStartMonth,
    ).start;
    const daysToMonday = anchor.day() === 0 ? 6 : anchor.day() - 1;
    const start = anchor.startOf('day').subtract(daysToMonday, 'day');
    const weekNumber =
        Math.floor(
            anchor.startOf('day').diff(yearStart.startOf('day'), 'day') / 7,
        ) + 1;

    return {
        start,
        end: start.add(6, 'day').endOf('day'),
        weekNumber,
    };
}

function sameWeekLastYear(
    weekStart: Dayjs,
    weekNumber: number,
    financialYearStartMonth: number,
): Dayjs {
    const previousYearAnchor = financialYearBounds(
        weekStart,
        financialYearStartMonth,
    ).start.subtract(1, 'day');
    const previousYear = financialYearBounds(
        previousYearAnchor,
        financialYearStartMonth,
    );
    const openingDay = previousYear.start.day();
    const daysToMonday = openingDay === 0 ? 6 : openingDay - 1;
    const candidate = previousYear.start
        .subtract(daysToMonday, 'day')
        .add((weekNumber - 1) * 7, 'day')
        .startOf('day');
    if (candidate.isBefore(previousYear.end)) {
        return candidate;
    }
    const endDay = previousYear.end.day();
    const daysFromMonday = endDay === 0 ? 6 : endDay - 1;
    return previousYear.end.startOf('day').subtract(daysFromMonday, 'day');
}

const COMPLETED_FINANCIAL_WEEK_COUNT = 4;

function toWindow(week: {
    start: Dayjs;
    end: Dayjs;
    weekNumber: number;
}): FinancialWeekWindow {
    return {
        start: week.start.toDate(),
        end: week.end.toDate(),
        weekNumber: week.weekNumber,
    };
}

export function getLastCompletedFinancialWeekWindows(
    anchor: string | Date,
    financialYearStartMonth = 7,
): {
    thisYearWeeks: FinancialWeekWindow[];
    lastYearWeeks: FinancialWeekWindow[];
} {
    const newestFirst: FinancialWeekWindow[] = [];
    let cursor = inJohannesburg(anchor);

    for (let index = 0; index < COMPLETED_FINANCIAL_WEEK_COUNT; index += 1) {
        const containing = weekContaining(cursor, financialYearStartMonth);
        const completed = weekContaining(
            containing.start.subtract(1, 'day'),
            financialYearStartMonth,
        );
        newestFirst.push(toWindow(completed));
        cursor = completed.start;
    }

    const thisYearWeeks = [...newestFirst].reverse();
    const lastYearWeeks = thisYearWeeks.map(week =>
        toWindow(
            weekContaining(
                sameWeekLastYear(
                    inJohannesburg(week.start),
                    week.weekNumber,
                    financialYearStartMonth,
                ),
                financialYearStartMonth,
            ),
        ),
    );

    return { thisYearWeeks, lastYearWeeks };
}

export type FinancialWeekSpan = {
    startDate: Date;
    endDate: Date;
};

function spanOf(weeks: FinancialWeekWindow[]): FinancialWeekSpan {
    return weeks.reduce(
        (span, week) => ({
            startDate:
                week.start < span.startDate ? week.start : span.startDate,
            endDate: week.end > span.endDate ? week.end : span.endDate,
        }),
        { startDate: weeks[0].start, endDate: weeks[0].end },
    );
}

export function getLastCompletedFinancialWeekSpan(
    anchor: string | Date,
    financialYearStartMonth = 7,
): { thisYear: FinancialWeekSpan; lastYear: FinancialWeekSpan } {
    const { thisYearWeeks, lastYearWeeks } =
        getLastCompletedFinancialWeekWindows(anchor, financialYearStartMonth);
    return {
        thisYear: spanOf(thisYearWeeks),
        lastYear: spanOf(lastYearWeeks),
    };
}
